import { AspectRatio } from '@lumen5/luminary';
import type { TransitionManager } from '@lumen5/luminary/core/transition/TransitionManager';

export const NONE_TRANSITION_URL = null;
export const NONE_GROUP_ID = 'NONE';
export const RECOMMENDED_TRANSITION_URL = 'RECOMMENDED';
export const RECOMMENDED_GROUP_ID = RECOMMENDED_TRANSITION_URL;

// Not a real transition URL — morph lives on the previous scene as `morphEnabled`, not on a gap's
// `transitionUrl`. The transitions tab just presents it as one more option.
export const MORPH_GROUP_ID = 'MORPH';

// Also not a transition URL: the same `morphEnabled` link, presented for the case where the two
// scenes share the same media and the morph reads as a zoom/pan within it.
export const ANIMATED_ZOOM_GROUP_ID = 'ANIMATED_ZOOM';

export type TransitionUrl = TransitionManager['transitionUrl'];
export type SelectTransitionHandler = (url: TransitionUrl) => void;

export enum Direction {
    LEFT = 'Left',
    RIGHT = 'Right',
    DOWN = 'Down',
    UP = 'Up',
    OUT = 'Out',
    IN = 'In',
}

export enum Shape {
    CIRCLE = 'Circle',
    SQUARE = 'Square',
    RECTANGLE = 'Rectangle',
    RIPPLE = 'Ripple',
}

export enum Duration {
    SHORT = 'Short',
    NORMAL = 'Normal',
    LONG = 'Long',
}

export type TransitionOption = Direction | Shape | Duration;

type AspectRatioUrls = Record<AspectRatio, string>;

export type TransitionUrlData = {
    groupId: string;
    optionId: TransitionOption;
    aspectRatio: AspectRatio;
};

export const TRANSITIONS: Record<
    string,
    Partial<Record<TransitionOption, Record<AspectRatio, string>>>
> = {};
export const TRANSITION_URL_DATA: Record<string, TransitionUrlData> = {};
export const TRANSITION_GROUP_IDS: string[] = [];
export const TRANSITION_LABELS: Record<string, string> = {};

type TransitionEntry = {
    label: string;
    options: Array<[TransitionOption, AspectRatioUrls]>;
};

const addTransition = ({ label, options }: TransitionEntry) => {
    const transitionId = label.split(' ').join('-');
    if (!TRANSITIONS[transitionId]) {
        TRANSITIONS[transitionId] = {};
        TRANSITION_GROUP_IDS.push(transitionId);
        TRANSITION_LABELS[transitionId] = label;
    }
    for (const [option, urls] of options) {
        if (!TRANSITIONS[transitionId][option]) {
            TRANSITIONS[transitionId][option] = {} as Record<AspectRatio, string>;
        }
        for (const [aspectRatio, url] of Object.entries(urls) as Array<[AspectRatio, string]>) {
            TRANSITIONS[transitionId][option][aspectRatio] = url;
            TRANSITION_URL_DATA[url] = {
                groupId: transitionId,
                optionId: option,
                aspectRatio,
            };
        }
    }
};

const addAspectRatio = (
    landscape: string,
    square: string,
    vertical: string,
    fourfive: string,
): AspectRatioUrls => ({
    [AspectRatio.LANDSCAPE]: landscape,
    [AspectRatio.SQUARE]: square,
    [AspectRatio.VERTICAL]: vertical,
    [AspectRatio.FOURFIVE]: fourfive,
});

const addOption = (
    option: TransitionOption,
    urls: AspectRatioUrls,
): [TransitionOption, AspectRatioUrls] => [option, urls];

const OPTION_DISPLAY_ORDER: TransitionOption[] = [
    Direction.LEFT,
    Direction.RIGHT,
    Direction.UP,
    Direction.DOWN,
    Direction.OUT,
    Direction.IN,
    Shape.CIRCLE,
    Shape.SQUARE,
    Shape.RECTANGLE,
    Shape.RIPPLE,
    Duration.SHORT,
    Duration.NORMAL,
    Duration.LONG,
];

export const getTransitionOptions = (
    groupId: string,
    aspectRatio: AspectRatio,
): Array<{ option: TransitionOption; url: string }> => {
    const transition = TRANSITIONS[groupId] ?? {};
    return OPTION_DISPLAY_ORDER.flatMap((option) => {
        const url = transition[option]?.[aspectRatio];
        return url ? [{ option, url }] : [];
    });
};

const OPTION_AXES: Array<[Set<string>, string]> = [
    [new Set<string>(Object.values(Shape)), 'Shape'],
    [new Set<string>(Object.values(Duration)), 'Duration'],
];

export const getTransitionOptionsLabel = (groupId: string): string => {
    const options = Object.keys(TRANSITIONS[groupId] ?? {});
    const axis = OPTION_AXES.find(([values]) => options.some((option) => values.has(option)));
    return axis ? axis[1] : 'Direction';
};

export const getTransitionUrlForOption = (
    groupId: string,
    aspectRatio: AspectRatio,
    option: TransitionOption | undefined,
): TransitionUrl => {
    if (option) {
        const url = TRANSITIONS[groupId]?.[option]?.[aspectRatio];
        if (url) return url;
    }
    return getDefaultTransitionUrl(groupId, aspectRatio);
};

const DEFAULT_OPTION_PRIORITY: TransitionOption[] = [
    Direction.OUT,
    Direction.IN,
    Direction.LEFT,
    Direction.RIGHT,
    Direction.UP,
    Direction.DOWN,
    Shape.CIRCLE,
    Shape.SQUARE,
    Shape.RECTANGLE,
    Shape.RIPPLE,
    Duration.NORMAL,
    Duration.SHORT,
    Duration.LONG,
];

export const getDefaultTransitionUrl = (
    groupId: string,
    aspectRatio: AspectRatio,
): TransitionUrl => {
    const transition = TRANSITIONS[groupId];
    if (!transition) return null;
    for (const option of DEFAULT_OPTION_PRIORITY) {
        const url = transition[option]?.[aspectRatio];
        if (url) return url;
    }
    return null;
};

// All available transitions should be added below using the addTransition function.

addTransition({
    label: 'Arrow Wipe',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Circle Wipe',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Diagonal Forward Wipe',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Diagonal Wipe',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Fade',
    options: [
        addOption(
            Duration.NORMAL,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-P.json',
            ),
        ),
        addOption(
            Duration.SHORT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-P.json',
            ),
        ),
        addOption(
            Duration.LONG,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Iris',
    options: [
        addOption(
            Shape.CIRCLE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/iris-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-P.json',
            ),
        ),
        addOption(
            Shape.SQUARE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/irisSquare-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-P.json',
            ),
        ),
        addOption(
            Shape.RECTANGLE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRectangle/Iris-Rectangle-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRectangle/Iris-Rectangle-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRectangle/Iris-Rectangle-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRectangle/Iris-Rectangle-P.json',
            ),
        ),
        addOption(
            Shape.RIPPLE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRippleV2/Iris-Ripple-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRippleV2/iris-Ripple-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRippleV2/Iris-Ripple-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisRippleV2/Iris-Ripple-P2.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Offset Push',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Push',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-L-V5.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-S-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-V-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Slide',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-P.json',
            ),
        ),
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-P.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Wipe',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-LeftV2/Wipe-Left-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-LeftV2/Wipe-Left-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-LeftV2/Wipe-Left-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-LeftV2/Wipe-Left-P2.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-RightV2/Wipe-Right-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-RightV2/Wipe-Right-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-RightV2/Wipe-Right-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-RightV2/Wipe-Right-P2.json',
            ),
        ),
        addOption(
            Direction.OUT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Vertical/Wipe-Center-Ver-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Vertical/Wipe-Center-Ver-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Vertical/Wipe-Center-Ver-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Vertical/Wipe-Center-Ver-P.json',
            ),
        ),
        addOption(
            Direction.IN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Ver-Rev/Wipe-Center-Ver-Rev-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Ver-Rev/Wipe-Center-Ver-Rev-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Ver-Rev/Wipe-Center-Ver-Rev-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Ver-Rev/Wipe-Center-Ver-Rev-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Horizontal Wipe',
    options: [
        addOption(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-UpV2/Wipe-Up-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-UpV2/Wipe-Up-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-UpV2/Wipe-Up-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-UpV2/Wipe-Up-P2.json',
            ),
        ),
        addOption(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-P.json',
            ),
        ),
        addOption(
            Direction.OUT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Horizontal/Wipe-Center-Hor-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Horizontal/Wipe-Center-Hor-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Horizontal/Wipe-Center-Hor-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Horizontal/Wipe-Center-Hor-P.json',
            ),
        ),
        addOption(
            Direction.IN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Hor-Rev/Wipe-Center-Hor-Rev-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Hor-Rev/Wipe-Center-Hor-Rev-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Hor-Rev/Wipe-Center-Hor-Rev-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center-Hor-Rev/Wipe-Center-Hor-Rev-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Wipe Bars',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left-BarsV2/Wipe-Left-Bars-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left-BarsV2/Wipe-Left-Bars-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left-BarsV2/Wipe-Left-Bars-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left-BarsV2/Wipe-Left-Bars-P2.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right-BarsV2/Wipe-Right-Bars-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right-BarsV2/Wipe-Right-Bars-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right-BarsV2/Wipe-Right-Bars-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right-BarsV2/Wipe-Right-Bars-P2.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Light Leaks',
    options: [
        addOption(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lightleaks2/Lightleaks2-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lightleaks2/Lightleaks2-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lightleaks2/Lightleaks2-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lightleaks2/Lightleaks2-P.json',
            ),
        ),
        addOption(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lighleaks/Lightleaks-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lighleaks/Lightleaks-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lighleaks/Lightleaks-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Lighleaks/Lightleaks-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Zoom',
    options: [
        addOption(
            Direction.OUT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-OutV2/Zoom-Out-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-OutV2/Zoom-Out-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-OutV2/Zoom-Out-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-OutV2/Zoom-Out-P2.json',
            ),
        ),
        addOption(
            Direction.IN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-InV2/Zoom-In-L2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-InV2/Zoom-In-S2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-InV2/Zoom-In-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Zoom-InV2/Zoom-In-P2.json',
            ),
        ),
    ],
});
